import { z } from 'zod';
/** Lienzos de los tres formatos (D6). */
export const LIENZOS = {
    R4_5: { w: 1080, h: 1350 },
    R1_1: { w: 1080, h: 1080 },
    R9_16: { w: 1080, h: 1920 },
};
export const zFormatoPoster = z.enum(['R4_5', 'R1_1', 'R9_16']);
// ── Piezas comunes (§5.1) ────────────────────────────────────────────────────
export const zSombra = z.object({
    x: z.number().default(0),
    y: z.number().default(4),
    desenfoque: z.number().default(12),
    color: z.string().default('rgba(0,0,0,0.35)'),
});
export const zTrazo = z.object({
    grosor: z.number().min(0).max(40),
    color: z.string(),
});
export const zRelleno = z.union([
    z.object({ tipo: z.literal('solido'), color: z.string() }),
    z.object({
        tipo: z.literal('degradado'),
        desde: z.string(),
        hasta: z.string(),
        angulo: z.number().default(180),
    }),
]);
export const zAnclaje = z.object({
    h: z.enum(['izquierda', 'centro', 'derecha', 'estirar']),
    v: z.enum(['arriba', 'centro', 'abajo', 'estirar']),
    escala: z.enum(['ancho', 'alto', 'menor', 'ninguna']),
});
/** D22: el cliente puede modificar todo, pero nada se puede deformar (§5.5). */
export const zRestricciones = z.object({
    proporcionFija: z.boolean(),
    dentroDelLienzo: z.boolean(),
    tamanoMin: z.number().min(0).max(100),
    tamanoMax: z.number().min(0).max(100),
    rotable: z.boolean(),
});
export const zRect = z.object({
    x: z.number(),
    y: z.number(),
    w: z.number().positive(),
    h: z.number().positive(),
});
const zBase = z.object({
    id: z.string().min(1), // estable entre los 3 formatos: es lo que permite "Adaptar"
    nombre: z.string(),
    rect: zRect, // % del lienzo
    rotacion: z.number().default(0),
    z: z.number().int().default(0),
    opacidad: z.number().min(0).max(1).default(1),
    visible: z.boolean().default(true),
    anclaje: zAnclaje,
    bloqueado: z.boolean().default(false), // sólo logo de marca / decoración fija
    /// El cliente puede ponerlo o quitarlo (p. ej. insignia de descuento). */
    opcional: z.boolean().default(false),
    restricciones: zRestricciones,
});
export const zCanalContacto = z.enum(['WHATSAPP', 'INSTAGRAM', 'FACEBOOK', 'TIKTOK']);
// ── Elementos (§5.1) ────────────────────────────────────────────────────────
export const zElementoTexto = zBase.extend({
    tipo: z.literal('texto'),
    contenido: z.string().max(500),
    binding: z.enum(['titulo', 'subtitulo', 'insignia', 'nombreNegocio', 'libre']).default('libre'),
    fuenteId: z.string(),
    tamano: z.number().min(8).max(400), // px sobre el lienzo de 1080
    peso: z.number().int().min(100).max(900).default(700),
    italic: z.boolean().default(false),
    color: z.string(),
    align: z.enum(['left', 'center', 'right']).default('center'),
    interlineado: z.number().min(0.7).max(3).default(1.15),
    transform: z.enum(['upper', 'lower', 'none']).default('none'),
    autoAjustar: z.boolean().default(true),
    maxLineas: z.number().int().min(1).max(10).default(2),
    sombra: zSombra.optional(),
    trazo: zTrazo.optional(),
});
export const zElementoProducto = zBase.extend({
    tipo: z.literal('producto'),
    indice: z.union([z.literal(0), z.literal(1), z.literal(2)]),
    ajuste: z.literal('contain'), // las fotos son cuadradas (D21)
    radio: z.number().min(0).max(200).default(0),
    sombra: zSombra.optional(),
});
export const zElementoPrecio = zBase.extend({
    tipo: z.literal('precio'),
    indice: z.union([z.literal(0), z.literal(1), z.literal(2)]), // ancla al producto (D15)
    estilo: z.enum(['simple', 'antes_ahora']).default('simple'),
    fuenteId: z.string(),
    tamanoAhora: z.number().min(8).max(300),
    tamanoAntes: z.number().min(8).max(200).default(28),
    colorAhora: z.string(),
    colorAntes: z.string().default('#9ca3af'),
    prefijo: z.string().max(8).default('$'),
    decimales: z.union([z.literal(0), z.literal(2)]).default(2),
    fondo: zRelleno.optional(),
    radio: z.number().min(0).max(200).default(0),
});
export const zElementoLogoCliente = zBase.extend({
    tipo: z.literal('logoCliente'),
    ajuste: z.literal('contain'),
    fondo: zRelleno.optional(),
    radio: z.number().min(0).max(200).default(0),
});
export const zElementoLogoMarca = zBase.extend({
    tipo: z.literal('logoMarca'),
    ajuste: z.literal('contain'),
    /** Tinte del logo (exige logo SVG): original, colores de la marca o plano. */
    color: z.enum(['original', 'primario', 'secundario', 'blanco', 'negro']).default('original'),
});
export const zElementoContactos = zBase.extend({
    tipo: z.literal('contactos'),
    canales: z.array(zCanalContacto).min(1),
    direccion: z.enum(['fila', 'columna']).default('fila'),
    fuenteId: z.string(),
    tamano: z.number().min(8).max(120),
    color: z.string(),
    mostrarIconos: z.boolean().default(true),
    separador: z.string().max(6).default('  ·  '),
});
export const zElementoImagen = zBase.extend({
    tipo: z.literal('imagen'),
    assetId: z.string(),
    ajuste: z.enum(['contain', 'cover']).default('contain'),
    radio: z.number().min(0).max(200).default(0),
});
export const zElementoImagenBase = zBase.extend({
    tipo: z.literal('imagenBase'),
    ajuste: z.literal('cover'), // foto de la galería a personalizar (D34)
});
export const zElementoVector = zBase.extend({
    tipo: z.literal('vector'),
    /** UNA capa de un SVG importado (markup saneado, sin el <svg> raíz). */
    svg: z.string().min(1).max(200_000),
    /** viewBox propio de la capa (su bounding box): la figura llena su caja. */
    viewBox: z.string().min(1),
    /** null = colores originales del SVG; acepta tokens de color de marca. */
    color: z.string().nullable().default(null),
});
export const zElementoForma = zBase.extend({
    tipo: z.literal('forma'), // D40
    forma: z.enum(['rect', 'circulo', 'linea']),
    relleno: zRelleno,
    borde: zTrazo.optional(),
    radio: z.number().min(0).max(200).default(0),
});
export const zPosterElement = z.discriminatedUnion('tipo', [
    zElementoTexto,
    zElementoProducto,
    zElementoPrecio,
    zElementoLogoCliente,
    zElementoLogoMarca,
    zElementoContactos,
    zElementoImagen,
    zElementoImagenBase,
    zElementoVector,
    zElementoForma,
]);
// ── PosterSpec: lo que manda el cliente. SOLO IDs, nunca URLs ni costos (§3.3). ──
export const zPosterSpec = z.object({
    plantillaId: z.string(),
    formato: zFormatoPoster,
    /** Bloques de productoIds; en modo simple hay un solo bloque (D23). */
    bloques: z.array(z.array(z.string()).min(1).max(3)).min(1).max(200),
    /** Elementos con las modificaciones del cliente. Los bloqueados se restauran en la API. */
    elementos: z.array(zPosterElement).optional(),
    fondoColor: z.string().optional(),
    logoClienteId: z.string().nullable().optional(),
    /** Variante del logo de marca elegida (asset de MarcaLogo); null = principal. */
    logoMarcaAssetId: z.string().nullable().optional(),
    /** Textos por binding: titulo, subtitulo, insignia. */
    textos: z.record(z.string().max(500)).optional(),
    /** Para plantillas PERSONALIZAR_IMAGEN: material de galería a usar de base. */
    materialId: z.string().optional(),
});
// ── Colores dinámicos de marca (los elementos guardan el token, no el color) ──
export const COLOR_MARCA_PRIMARIO = '@marcaPrimario';
export const COLOR_MARCA_SECUNDARIO = '@marcaSecundario';
/** Reemplaza los tokens de color de marca en cualquier campo de color de los elementos. */
export function aplicarColoresMarca(elementos, colores) {
    // Los tokens sólo existen como valor COMPLETO de un string JSON: el replace es seguro.
    const json = JSON.stringify(elementos)
        .replaceAll(JSON.stringify(COLOR_MARCA_PRIMARIO), JSON.stringify(colores.primario))
        .replaceAll(JSON.stringify(COLOR_MARCA_SECUNDARIO), JSON.stringify(colores.secundario));
    return JSON.parse(json);
}
/** Igual que aplicarColoresMarca pero para un color suelto (p. ej. el fondo). */
export function colorDeMarca(valor, colores) {
    if (valor === COLOR_MARCA_PRIMARIO)
        return colores.primario;
    if (valor === COLOR_MARCA_SECUNDARIO)
        return colores.secundario;
    return valor;
}
/**
 * Tiñe UNA capa SVG con un color plano: reemplaza fill/stroke que no sean
 * none/transparent y envuelve en <g fill> para que los paths sin fill hereden.
 */
export function recolorearFragmentoSvg(fragmento, color) {
    let s = fragmento;
    s = s.replace(/(fill|stroke)\s*=\s*["'](?!none\b|transparent\b)[^"']*["']/gi, (_m, attr) => `${attr}="${color}"`);
    s = s.replace(/(fill|stroke)\s*:\s*(?!none\b|transparent\b)[^;"'}]+/gi, (_m, attr) => `${attr}:${color}`);
    return `<g fill="${color}">${s}</g>`;
}
//# sourceMappingURL=tipos.js.map